import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { DateAdapter, MAT_DATE_LOCALE, ErrorStateMatcher, ShowOnDirtyErrorStateMatcher, MAT_DATE_FORMATS } from '@angular/material/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipDefaultOptions } from '@angular/material/tooltip';
import { MomentDateAdapter, MAT_MOMENT_DATE_FORMATS } from '@angular/material-moment-adapter';
import { NgxImageCompressService } from 'ngx-image-compress';
import { enableProdMode, provideAppInitializer, ErrorHandler, LOCALE_ID, importProvidersFrom, inject, provideZoneChangeDetection } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';

import { provideTransloco, TranslocoService } from '@jsverse/transloco';

import { AppComponent } from './app/app.component';
import { CoreModule } from './app/core/core.module';
import { ErrorReportingService } from './app/features/shared/error/error-reporting.service';
import { LanguageService } from './app/infrastructure/i18n/language.service';
import { NoopTranslationProvider, TRANSLATION_PROVIDER } from './app/infrastructure/i18n/translation-provider';
import { TranslationFallbackService } from './app/infrastructure/i18n/translation-fallback.service';
import { TranslocoHttpLoader } from './app/infrastructure/i18n/transloco-loader';
import { httpErrorInterceptor } from './app/auth/helpers/error.interceptor';
import { jwtUrlInterceptor } from './app/auth/helpers/jwt-url-interceptor';
import { progressInterceptor } from './app/auth/helpers/progress.interceptor';
import { responseInterceptor } from './app/auth/helpers/response.interceptor';
import { appInitializer } from './app/auth/helpers/app-inintializer';
import { environment } from './environments/environment';
import { AuthService } from './app/auth/services/auth.service';
import { provideRouter, RouteReuseStrategy, withComponentInputBinding, withViewTransitions } from '@angular/router';
import { ParamAwareReuseStrategy } from './app/infrastructure/re-user-strategy/re-use-strategy';
import { routes as appRoutes } from './app/app-routes';
import { firstValueFrom } from 'rxjs';
import { ConfigService } from './app/infrastructure/services/config-services/config.service';
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';

const defOptions: MatTooltipDefaultOptions = {
    showDelay: 1000,
    hideDelay: 0,
    touchendHideDelay: 500
};

if (environment.production) {
    enableProdMode();
}

bootstrapApplication(AppComponent, {
    providers: [
        provideZoneChangeDetection(),importProvidersFrom(
            BrowserModule,
            // HttpClientModule,
            // AppRoutingModule,
            // BrowserAnimationsModule,
            CoreModule,
            // NgxPicaModule,
            FontAwesomeModule
        ),
        provideRouter(appRoutes, withComponentInputBinding(), withViewTransitions()),
        provideAppInitializer(async () => {
            const configService = injectRequired(ConfigService);
            const authService = injectRequired(AuthService);
            const languageService = injectRequired(LanguageService);
            const translationFallback = injectRequired(TranslationFallbackService);
            const transloco = injectRequired(TranslocoService);

            // Taal bepalen én de vertalingen daadwerkelijk inladen (van /assets, niet de API)
            // vóór de eerste API-calls. Anders tonen fout-notificaties bij een onbereikbare
            // server de sleutel (errors.x.text) i.p.v. de vertaalde tekst.
            languageService.init();
            await firstValueFrom(transloco.load(transloco.getActiveLang()));
            translationFallback.init();

            await configService.loadConfig();
            await firstValueFrom(authService.refreshToken$());
        }),
        provideHttpClient(
            withInterceptors([
                httpErrorInterceptor,
                jwtUrlInterceptor,
                responseInterceptor,
                progressInterceptor,
            ])
        ),
        provideTransloco({
            config: {
                availableLangs: ['nl', 'en'],
                defaultLang: 'nl',
                fallbackLang: 'nl',
                reRenderOnLangChange: true,
                prodMode: environment.production,
                missingHandler: { useFallbackTranslation: true },
            },
            loader: TranslocoHttpLoader,
        }),
        // Machine-vertaal-provider; vervang NoopTranslationProvider door een echte dienst (DeepL/Google/LibreTranslate).
        { provide: TRANSLATION_PROVIDER, useExisting: NoopTranslationProvider },
        // CanvasWhiteboardShapeService,
        NgxImageCompressService,
        // { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: snackBarOptions},
        // CustomReuseStrategy (per-pad caching) blijft uit; wel een param-aware reuse zodat
        // record→record-navigatie (bv. order A → order B) de component echt herbouwt.
        { provide: RouteReuseStrategy, useClass: ParamAwareReuseStrategy },
        provideAnimationsAsync(),
        // { provide: APP_INITIALIZER, useFactory: appInitializer, multi: true, deps: [AuthService] },
        { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
        { provide: ErrorHandler, useExisting: ErrorReportingService },
        { provide: ErrorStateMatcher, useClass: ShowOnDirtyErrorStateMatcher },
        { provide: LOCALE_ID, useValue: 'nl-NL' },
        { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS },
        { provide: MAT_DATE_LOCALE, useValue: 'nl-NL' },
        { provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: defOptions },
    ]
})
    .catch(err => console.error(err));
